import {ApolloClient} from "apollo-client"
import {onError} from "apollo-link-error"
import {InMemoryCache} from "apollo-cache-inmemory"
import {HttpLink} from "apollo-link-http"
import isRPDeal from "@irati/deals/helpers/deals/isRPDeal"
import buildAxiosFetch from "../../axios-fetch"
import graphFetch from "../graph/fetch"
import typeDefs from "./schema"
import {apolloLogger} from "./middleware/apolloLogger"
import {apolloErrorHandlerLink} from "./middleware/errorHandler"
import {omitTypenameLink} from "./middleware/omitTypename"

const formatCacheKey = (type: string, id: string) => `${type}@${id}`

/**
 * @description caching SlimDeals by their uuid will cause duplicates to be stored if there are
 * multiple locations for the same deal. Using the deal's location uuid ensures each deal is unique.
 */
const formatSlimDealCacheKey = (locationId: string) => {
  return `SlimDeal@${locationId}`
}

export const cache = new InMemoryCache({
  dataIdFromObject: (object: any) => {
    if (object.__typename === "SlimDeal") {
      return formatSlimDealCacheKey(object.location.uuid)
    }
    if (object.__typename === "Product" || object.__typename === "DealProductSnapshot") {
      return null
    }

    if (object.__typename === "Deal" && isRPDeal(object)) {
      return formatCacheKey(object.__typename, `${object.uuid}@RewardPoint`)
    }
    if (object.__typename === "Budget" || object.__typename === "BudgetUnassigned") {
      return formatCacheKey(object.__typename, `${object.walletId}_${object.uuid}`)
    }
    return !object.uuid && !object.id ? null : formatCacheKey(object.__typename, object.uuid || object.id)
  },
  cacheRedirects: {
    Query: {
      poll: (_, args, {getCacheKey}) =>
        getCacheKey({
          uuid: args.id,
          __typename: "Poll"
        }),
      recognition: (_, args, {getCacheKey}) =>
        getCacheKey({
          uuid: args.id,
          __typename: "Recognition"
        })
    }
  }
})

/**
 * The wrapper Error that will be returned from any Apollo component
 * or hook
 */
export interface PBClientApolloError extends Error {
  graphQLErrors: []
  networkError: GraphError
}

const client = new ApolloClient({
  // an apollo 'link' is like middleware, you build up a link of
  // functions and the requests flow through and then return back
  // along the link, so order is important!
  link:
    // NOTE: inlining because it's important
    // this must come first to capture any errors that occured and
    // feed them down into components or hooks
    // this will return any errors in a PBClientApolloError
    //
    // See the apolloErrorHandler for how we throw an errors the occurred
    // further up the link
    onError(({graphQLErrors, networkError}) => {
      if (graphQLErrors) {
        // empty for now
      }

      if (networkError) {
        console.log("[Network error]:", networkError)
      }
    })
      .concat(omitTypenameLink)
      .concat(apolloErrorHandlerLink)
      .concat(apolloLogger())
      .concat(
        new HttpLink({
          fetch: buildAxiosFetch(graphFetch())
        })
      ),
  cache,
  typeDefs
})

export default client
